import { getIvaRateVenta } from './getIvaRateVenta';
import { truncToDecimals } from './formatters';

export interface LineaIvaVenta {
  codigoArticulo: string;
  subtotal: number;
}

export interface ImpuestoPorTasaVenta {
  tasa: number;
  monto: number;
}

export interface TotalesIvaVenta {
  subtotalConDesc: number;
  impuestosArray: ImpuestoPorTasaVenta[];
  montoIVA: number;
  total: number;
}

const round2 = (n: number) => truncToDecimals(n, 2);

/**
 * Calcula IVA por línea (con reparto proporcional de descuentos globales) y agrupa por alícuota.
 * Misma lógica que NuevaFacturaPOS / NotaCreditoForm / FacturaForm (resumen).
 */
export function calcularTotalesIvaVenta(params: {
  lineas: LineaIvaVenta[];
  subtotalNeto: number;
  subtotalConDesc: number;
  tipoIva: string;
  productosCache: Record<string, unknown>;
  condIvaEmisor?: string | null;
}): TotalesIvaVenta {
  const { lineas, subtotalNeto, subtotalConDesc, tipoIva, productosCache, condIvaEmisor } = params;
  const tipo = (tipoIva || '').trim().toUpperCase();

  if (subtotalNeto <= 0 || subtotalConDesc <= 0) {
    return {
      subtotalConDesc: round2(subtotalConDesc),
      impuestosArray: [],
      montoIVA: 0,
      total: round2(subtotalConDesc),
    };
  }

  const impuestosPorTasa = new Map<number, { tasa: number; monto: number }>();

  for (const l of lineas) {
    const codigo = String(l.codigoArticulo ?? '').trim();
    if (!codigo || l.subtotal <= 0) continue;

    const share = l.subtotal / subtotalNeto;
    const netAfterGlobalDesc = subtotalConDesc * share;
    const producto = productosCache[codigo];
    const ivaRate = getIvaRateVenta(producto, tipo, condIvaEmisor);
    const tasaIVA = round2(ivaRate * 100);
    const montoIVA = round2(netAfterGlobalDesc * ivaRate);

    if (montoIVA <= 0 || tasaIVA <= 0) continue;

    const existente = impuestosPorTasa.get(tasaIVA);
    if (existente) {
      existente.monto = round2(existente.monto + montoIVA);
    } else {
      impuestosPorTasa.set(tasaIVA, { tasa: tasaIVA, monto: montoIVA });
    }
  }

  const impuestosArray = Array.from(impuestosPorTasa.values())
    .filter((imp) => imp.tasa > 0 && imp.monto > 0)
    .sort((a, b) => b.tasa - a.tasa);

  const montoIVA = round2(impuestosArray.reduce((sum, imp) => sum + imp.monto, 0));
  const total = round2(subtotalConDesc + montoIVA);

  return {
    subtotalConDesc: round2(subtotalConDesc),
    impuestosArray,
    montoIVA,
    total,
  };
}
